import { useCallback, useMemo } from "react";
import { Modal, ModalContent, ModalHeader, ModalBody, Spinner, Image } from "@heroui/react";
import { Trash, EnvelopeSimple } from "@phosphor-icons/react";
import { UniversalTable } from "shared/components/UniversalTable/UniversalTable";
import type { CellConfig } from "shared/components/UniversalTable/components";
import { TablePagination } from "components/TablePagination";
import { KpiIndicatorBar } from "./KpiIndicatorBar";
import ModalCandidateDetail from "../../screens/buildings/components/rentalTab/modalCandidateDetail";
import { CandidateEmailModal } from "./email";
import { AlternateDateSelectionModal } from "../modals/AlternateDateSelectionModal";
import { ManualAppointmentModal } from "../modals/ManualAppointmentModal";
import { ArchiveCandidateModal } from "./archiveCandidateModal";
import FollowUpModal from "./follow-up/FollowUpModal";
import { CANDIDATE_TABLE_CONFIG } from "./configs/candidateTableConfig";
import { createCandidateCells, type CandidateCellHandlers } from "./configs/candidateCellFactory";
import { useCandidateTableData } from "./hooks/useCandidateTableData";
import type { CandidateInfo, CandidateTableProps } from "./types";

export const CandidateTableView = ({
  buildingId,
  handleOpenAddContractModal,
  filters,
  includeArchived = false,
  onCandidateClick,
}: CandidateTableProps) => {
  const data = useCandidateTableData({
    buildingId,
    filters,
    includeArchived,
    onCandidateClick,
  });

  // Build a Set<number> for UniversalTable selection (it expects numeric IDs)
  const selectedRowsSet = useMemo(() => {
    const numSet = new Set<number>();
    for (const id of data.selectedIds) {
      const n = Number(id);
      if (!isNaN(n)) numSet.add(n);
    }
    return numSet;
  }, [data.selectedIds]);

  const handleSelectionChange = useCallback(
    (newSelection: Set<number>) => {
      const strSet = new Set<string>();
      for (const n of newSelection) strSet.add(String(n));
      data.setSelectedIds(strSet);
    },
    [data.setSelectedIds],
  );

  // Cell handlers for the factory
  const cellHandlers: CandidateCellHandlers = useMemo(
    () => ({
      onToggleSelect: () => {}, // handled by context from UniversalTable
      onOpenDetail: data.handleOpenCandidateDetailModal,
      onManageCandidate: data.handleManageCandidate,
      onDocumentPreview: data.handleOpenDocumentPreview,
      onSelectAlternateDate: data.handleOpenDateSelectionModal,
      onDisapproveAlternates: data.handleDisapproveAlternates,
      onOpenManualAppointment: data.handleOpenManualAppointmentModal,
      onDeleteArchive: data.handleOpenArchiveModal,
      onFollowUpClick: data.handleFollowUpClick,
      onLogPhoneCall: data.handleLogPhoneCall,
    }),
    [
      data.handleOpenCandidateDetailModal,
      data.handleManageCandidate,
      data.handleOpenDocumentPreview,
      data.handleOpenDateSelectionModal,
      data.handleDisapproveAlternates,
      data.handleOpenManualAppointmentModal,
      data.handleOpenArchiveModal,
      data.handleFollowUpClick,
      data.handleLogPhoneCall,
    ],
  );

  // renderRow callback for UniversalTable
  const renderRow = useCallback(
    (row: CandidateInfo, context: any): CellConfig[] => {
      return createCandidateCells({
        candidate: row,
        context,
        handlers: cellHandlers,
        unitInfo: data.unitInfo,
      });
    },
    [cellHandlers, data.unitInfo],
  );

  // Bulk actions for the search config bar
  const bulkActions = useMemo(
    () => [
      {
        label: "Afwijzen met e-mail",
        icon: <EnvelopeSimple size={14} />,
        onClick: data.handleBulkRejectWithEmail,
        className:
          "flex items-center gap-1.5 rounded-md bg-amber-50 px-3 py-1.5 text-sm font-medium text-amber-700 transition-colors hover:bg-amber-100 disabled:opacity-50",
      },
      {
        label: "Archiveren",
        icon: <Trash size={14} />,
        onClick: data.handleBulkArchive,
        className:
          "flex items-center gap-1.5 rounded-md bg-red-50 px-3 py-1.5 text-sm font-medium text-red-700 transition-colors hover:bg-red-100 disabled:opacity-50",
      },
    ],
    [data.handleBulkRejectWithEmail, data.handleBulkArchive],
  );

  return (
    <>
      {/* KPI bar + pagination on same row */}
      <div className="flex items-center justify-between gap-2">
        <KpiIndicatorBar
          interestedCount={data.kpiCounts.interestedCount}
          notFollowedUpCount={data.kpiCounts.notFollowedUpCount}
          staleCount={data.kpiCounts.staleCount}
          awaitingInterestedCount={data.kpiCounts.awaitingInterestedCount}
          notInterestedCount={data.kpiCounts.notInterestedCount}
          activeFilter={data.activeKpiFilter}
          onFilterClick={data.handleKpiFilterClick}
          isLoading={data.isLoading}
        />
        {data.totalCount > 0 && (
          <div className="shrink-0">
            <TablePagination
              page={data.page}
              pageSize={data.pageSize}
              totalCount={data.totalCount}
              onPageChange={data.setPage}
              isLoading={data.isLoading}
            />
          </div>
        )}
      </div>

      {/* Bulk action bar */}
      {data.selectedIds.size > 0 && (
        <div className="flex items-center gap-3 rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-1.5">
          <span className="text-sm font-medium text-neutral-700">
            {data.selectedIds.size} geselecteerd
          </span>
          <button
            onClick={() => data.setSelectedIds(new Set())}
            className="text-sm text-neutral-500 underline hover:text-neutral-700"
            type="button"
          >
            Selectie wissen
          </button>
          <button
            onClick={data.handleBulkRejectWithEmail}
            disabled={data.isProcessing}
            className="ml-auto flex items-center gap-1.5 rounded-md bg-amber-50 px-3 py-1.5 text-sm font-medium text-amber-700 transition-colors hover:bg-amber-100 disabled:opacity-50"
            type="button"
          >
            <EnvelopeSimple size={14} />
            Afwijzen met e-mail
          </button>
          <button
            onClick={data.handleBulkArchive}
            disabled={data.isProcessing}
            className="flex items-center gap-1.5 rounded-md bg-red-50 px-3 py-1.5 text-sm font-medium text-red-700 transition-colors hover:bg-red-100 disabled:opacity-50"
            type="button"
          >
            <Trash size={14} />
            Archiveren
          </button>
        </div>
      )}

      {/* Table — override UniversalTable's internal spacing */}
      <div className="candidate-table-wrap">
        <UniversalTable
          columnsConfig={CANDIDATE_TABLE_CONFIG.columnsConfig}
          sortConfig={CANDIDATE_TABLE_CONFIG.sortConfig}
          rows={data.candidates}
          groupBy="none"
          title=""
          isLoading={data.isLoading}
          renderRow={renderRow}
          selectedRows={selectedRowsSet}
          onSelectionChange={handleSelectionChange}
          getRowKey={(row: CandidateInfo) => Number(row.id)}
          searchConfig={{
            bulkActions,
            serverSideFiltering: true,
            showSpacer: false,
          }}
        />
      </div>

      {/* ===== MODALS ===== */}

      {/* Document preview modal */}
      <Modal
        isOpen={data.isDocumentPreviewOpen}
        onClose={data.handleCloseDocumentPreview}
        backdrop="blur"
        size="5xl"
        className="p-4"
      >
        <ModalContent>
          <ModalHeader className="flex items-center justify-between text-base font-semibold">
            <span>{data.documentPreviewTitle || "Documenten"}</span>
          </ModalHeader>
          <ModalBody className="p-6">
            {data.isDocumentPreviewLoading ? (
              <div className="flex justify-center py-10">
                <Spinner color="success" />
              </div>
            ) : data.documentPreview.length > 0 ? (
              <div className="flex max-h-[70vh] flex-row gap-4 overflow-x-auto">
                {data.documentPreview.map((doc, index) => {
                  const fileType = (doc.fileType || "").toLowerCase();
                  const key = `${doc.fileType}-${index}`;
                  return (
                    <div key={key} className="flex flex-col gap-2">
                      <div
                        className="cursor-pointer overflow-hidden rounded-lg border transition-shadow hover:shadow-lg"
                        onClick={() => data.handleExpandDocument(doc, index)}
                      >
                        {fileType === "pdf" ? (
                          <div className="group relative">
                            <iframe
                              src={`data:application/pdf;base64,${doc.image}`}
                              className="pointer-events-none h-96 w-full object-contain"
                              title={`${data.documentPreviewTitle || "Document"} ${index + 1}`}
                            />
                            <div className="group-hover:bg-opacity-10 absolute inset-0 bg-transparent transition-all group-hover:bg-black" />
                            <div className="bg-opacity-90 absolute top-2 right-2 rounded-sm bg-white px-2 py-1 text-xs font-medium opacity-0 transition-opacity group-hover:opacity-100">
                              Klik om te vergroten
                            </div>
                          </div>
                        ) : (
                          <div className="group relative">
                            <Image
                              src={`data:image/${doc.fileType};base64,${doc.image}`}
                              className="h-auto w-full object-contain"
                              alt={`${data.documentPreviewTitle || "Document"} ${index + 1}`}
                            />
                            <div className="group-hover:bg-opacity-10 absolute inset-0 bg-transparent transition-all group-hover:bg-black" />
                            <div className="bg-opacity-90 absolute top-2 right-2 rounded-sm bg-white px-2 py-1 text-xs font-medium opacity-0 transition-opacity group-hover:opacity-100">
                              Klik om te vergroten
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-neutral-500">Geen documenten beschikbaar.</p>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Expanded document modal */}
      <Modal
        isOpen={Boolean(data.expandedDocument)}
        onClose={data.handleCloseExpandedDocument}
        size="full"
        backdrop="blur"
        className="p-4"
      >
        <ModalContent>
          <ModalHeader className="flex items-center justify-between">
            <span>
              {data.documentPreviewTitle || "Document"}{" "}
              {data.expandedDocument ? data.expandedDocument.index + 1 : ""} –{" "}
              {data.expandedDocument?.fileType?.toUpperCase()}
            </span>
          </ModalHeader>
          <ModalBody className="flex items-center justify-center p-6">
            {data.expandedDocument && (
              <div className="flex h-full w-full items-center justify-center">
                {(data.expandedDocument.fileType || "").toLowerCase() === "pdf" ? (
                  <iframe
                    src={`data:application/pdf;base64,${data.expandedDocument.image}`}
                    className="h-[80vh] w-full rounded-lg border"
                    title={`${data.documentPreviewTitle || "Document"} ${data.expandedDocument.index + 1}`}
                  />
                ) : (
                  <Image
                    src={`data:image/${data.expandedDocument.fileType};base64,${data.expandedDocument.image}`}
                    className="max-h-[80vh] max-w-full rounded-lg border object-contain"
                    alt={`${data.documentPreviewTitle || "Document"} ${data.expandedDocument.index + 1}`}
                  />
                )}
              </div>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Candidate detail modal */}
      <ModalCandidateDetail
        isOpen={data.isCandidateDetailModalOpen}
        onClose={data.handleCloseCandidateDetailModal}
        candidateDetailInfo={data.candidateDetailInfo}
        onOpenAddContractModal={(c, unitId, totalResidents) => {
          handleOpenAddContractModal(c as any, unitId, undefined, totalResidents);
        }}
        onFollowUpChange={(candidateId, newStatus, remark) => {
          const candidate = data.candidates.find((c) => c.id === candidateId);
          if (candidate) {
            data.handleFollowUpChange(candidate, newStatus, remark);
            if (data.candidateDetailInfo?.id === candidateId) {
              data.setCandidateDetailInfo((prev: any) =>
                prev
                  ? {
                      ...prev,
                      followUpStatus: newStatus,
                      followUpRemark: newStatus === "problem" ? (remark || null) : null,
                    }
                  : prev,
              );
            }
          }
        }}
      />

      {/* Single candidate email modal */}
      <CandidateEmailModal
        isOpen={data.showEmailModal}
        onClose={data.handleCloseEmailModal}
        onSuccess={data.handleEmailSuccess}
        candidate={data.selectedCandidate}
        brokers={
          data.selectedCandidate?.unitId
            ? data.unitInfo.get(data.selectedCandidate.unitId)?.brokerContacts ?? []
            : []
        }
        buildingAddress={
          data.selectedCandidate?.unitId
            ? data.unitInfo.get(data.selectedCandidate.unitId)?.buildingAddress ?? null
            : null
        }
      />

      {/* Bulk email modal */}
      <CandidateEmailModal
        isOpen={data.showBulkEmailModal}
        onClose={() => data.setShowBulkEmailModal(false)}
        onSuccess={data.handleEmailSuccess}
        candidates={data.candidates.filter((c) => data.selectedIds.has(c.id))}
        defaultTemplateType="rejection"
      />

      {/* Alternate date selection modal */}
      <AlternateDateSelectionModal
        isOpen={data.isDateSelectionModalOpen}
        onClose={data.handleCloseDateSelectionModal}
        onConfirm={data.handleDateSelectionConfirm}
        alternateDates={data.selectedCandidateForDateSelection?.alternateDates || []}
        candidateName={`${data.selectedCandidateForDateSelection?.personalDetails?.firstName || ""} ${data.selectedCandidateForDateSelection?.personalDetails?.lastName || ""}`.trim()}
        isLoading={data.isSelectingDate}
      />

      {/* Manual appointment modal */}
      <ManualAppointmentModal
        isOpen={data.isManualAppointmentModalOpen}
        onClose={data.handleCloseManualAppointmentModal}
        onConfirm={data.handleManualAppointmentConfirm}
        candidateName={`${data.selectedCandidateForManualAppointment?.personalDetails?.firstName || ""} ${data.selectedCandidateForManualAppointment?.personalDetails?.lastName || ""}`.trim()}
        isLoading={data.isProcessing}
      />

      {/* Archive modal */}
      <ArchiveCandidateModal
        isOpen={data.showArchiveModal}
        onClose={() => {
          data.setShowArchiveModal(false);
          data.setSelectedCandidateForArchive(null);
        }}
        onConfirm={data.handleArchiveConfirm}
        candidate={data.selectedCandidateForArchive}
        isLoading={data.isProcessing}
        isInvited={
          typeof data.selectedCandidateForArchive?.isInvited === "boolean"
            ? data.selectedCandidateForArchive.isInvited
            : data.selectedCandidateForArchive?.status === "notify" ||
              data.selectedCandidateForArchive?.status === "invited" ||
              data.selectedCandidateForArchive?.status === "reminder-sent"
        }
      />

      {/* Follow-up modal */}
      {data.selectedCandidateForFollowUp && (
        <FollowUpModal
          isOpen={data.isFollowUpModalOpen}
          onClose={data.handleFollowUpModalClose}
          applicationId={Number(data.selectedCandidateForFollowUp.id)}
          candidateName={`${data.selectedCandidateForFollowUp.personalDetails?.firstName || ""} ${data.selectedCandidateForFollowUp.personalDetails?.lastName || ""}`.trim()}
          summary={
            data.followUpSummaries[data.selectedCandidateForFollowUp.id] ??
            data.selectedCandidateForFollowUp.followUpSummary ??
            null
          }
          onSummaryChange={data.handleFollowUpDataChanged}
        />
      )}
    </>
  );
};
